import { useState, type FormEvent } from 'react';
import { Camera, CircleX, Save, Trash2 } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { UserAvatar } from './UserAvatar';
import { VerificationBadge } from './VerificationBadge';
import { useSiteCopy } from '../content/SiteCopyContext';

async function compressAvatar(file: File) {
  if (!file.type.startsWith('image/')) throw new Error('请选择图片文件');
  if (file.size > 15 * 1024 * 1024) throw new Error('原图不能超过 15 MB');
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    await new Promise<void>((resolve, reject) => { image.onload = () => resolve(); image.onerror = () => reject(new Error('头像图片无法读取')); image.src = url; });
    const side = Math.min(image.naturalWidth, image.naturalHeight);
    const sx = (image.naturalWidth - side) / 2;
    const sy = (image.naturalHeight - side) / 2;
    for (const [size, quality] of [[256, .78], [224, .7], [192, .62]] as const) {
      const canvas = document.createElement('canvas'); canvas.width = size; canvas.height = size;
      canvas.getContext('2d')!.drawImage(image, sx, sy, side, side, 0, 0, size, size);
      const data = canvas.toDataURL('image/webp', quality);
      if (data.length <= 140 * 1024) return data;
    }
    throw new Error('头像压缩后仍然过大，请换一张图片');
  } finally { URL.revokeObjectURL(url); }
}

export function ProfileModal({ onClose }: { onClose: () => void }) {
  const { user, updateProfile } = useAuth();
  const copy = useSiteCopy();
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [avatarData, setAvatarData] = useState(user?.avatarData || '');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  if (!user) return null;
  async function submit(event: FormEvent) {
    event.preventDefault(); setSaving(true); setError('');
    try { await updateProfile({ displayName, bio, avatarData }); onClose(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : '保存失败'); }
    finally { setSaving(false); }
  }
  return <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}><section className="profile-modal" role="dialog" aria-modal="true" aria-labelledby="profile-modal-title">
    <button className="modal-close icon-button" type="button" onClick={onClose} aria-label="关闭"><CircleX size={19} /></button>
    <div className="profile-modal-head"><UserAvatar avatarData={avatarData} avatarColor={user.avatarColor} name={displayName || user.displayName} className="profile-avatar" /><div><p className="eyebrow">MY PROFILE</p><h2 id="profile-modal-title">{copy('profile.title')}</h2><span className="profile-account-line">@{user.username}{user.group ? ` · ${user.group.name}` : ''}<VerificationBadge type={user.verificationType} showLabel /></span></div></div>
    <form onSubmit={submit} className="profile-modal-form">
      <div className="avatar-actions"><label className="secondary-button"><Camera size={16} />{copy('profile.uploadAvatar')}<input type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(event) => { const file = event.target.files?.[0]; if (file) void compressAvatar(file).then(setAvatarData).catch((reason) => setError(reason.message)); }} /></label>{avatarData && <button className="text-button" type="button" onClick={() => setAvatarData('')}><Trash2 size={15} />{copy('profile.resetAvatar')}</button>}<small>{copy('profile.avatarNote')}</small></div>
      <label>{copy('profile.nickname')}<input value={displayName} onChange={(event) => setDisplayName(event.target.value)} maxLength={24} required /></label>
      {user.permissions.canUploadGallery && <label>{copy('profile.bioLabel')}<textarea value={bio} onChange={(event) => setBio(event.target.value)} maxLength={160} rows={4} placeholder={copy('profile.bioPlaceholder')} /></label>}
      {error && <p className="form-error" role="alert">{error}</p>}
      <button className="primary-button" type="submit" disabled={saving}><Save size={17} />{saving ? copy('profile.saving') : copy('profile.save')}</button>
    </form>
  </section></div>;
}
